<template>
  <div class="flex flex-col">
    <!--
      The add row as a card rather than four bare inputs: it is one action — a
      dated value with a kind — and the four controls of unequal width read as
      four unrelated questions without a frame and a caption around them.
    -->
    <section class="p-4.5 mb-5 bg-card border border-border rounded-ds-lg shadow-ds-sm">
      <h4 class="mb-2.5 text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
        Record a value
      </h4>

      <div class="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-[10rem_minmax(0,1fr)_9rem_auto] lg:items-end">
        <div class="min-w-0">
          <DSFieldLabel
            :id="dateLabelId"
            class="mb-1.5"
            label="As of"
          />
          <DSDateField
            v-model="draft.date"
            :aria-labelledby="dateLabelId"
          />
        </div>

        <div
          ref="valueCell"
          class="min-w-0"
        >
          <!--
            The currency beside the caption rather than inside the control:
            DSAmountField deliberately formats without a symbol, because the
            amount and the currency are separate fields everywhere in this app.
          -->
          <DSFieldLabel
            class="mb-1.5"
            :for="valueId"
          >
            Value
            <span
              v-if="currency"
              class="font-medium normal-case tracking-normal"
            >· {{ currency }}</span>
          </DSFieldLabel>
          <DSAmountField
            :id="valueId"
            v-model="draft.close"
            placeholder="0.00"
          />
        </div>

        <div class="min-w-0">
          <DSFieldLabel
            class="mb-1.5"
            :for="sourceId"
            label="Method"
          />
          <DSSelect
            v-model="draft.source"
          >
            <DSSelectTrigger :id="sourceId">
              <DSSelectValue />
            </DSSelectTrigger>
            <DSSelectContent>
              <DSSelectItem
                v-for="option in SOURCES"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </DSSelectItem>
            </DSSelectContent>
          </DSSelect>
        </div>

        <DSButton
          type="button"
          :disabled="!canAdd"
          @click="handleAdd"
        >
          Add value
        </DSButton>
      </div>

      <!--
        What the two methods actually do to the timeline, in the app's own
        words — the same rule the prices tab states in its tooltip. A method
        picker whose options are not explained is a choice the user cannot make.
      -->
      <p class="flex gap-2 mt-3 text-xs font-medium leading-normal text-muted-foreground">
        <Info class="size-3.5 shrink-0 mt-0.5" />
        <span>
          <strong class="font-bold text-foreground">Override</strong> replaces the carrying value
          from its date forward, and later marks are ignored until the next override.
          <br><strong class="font-bold text-foreground">Manual</strong> sets the value from its date
          until the next mark.
        </span>
      </p>
    </section>

    <template v-if="marks.length">
      <div class="flex items-baseline justify-between gap-3 mb-2.5">
        <h4 class="text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
          Value history · {{ marks.length }} {{ marks.length === 1 ? 'mark' : 'marks' }}
        </h4>
        <span class="text-xs font-medium text-muted-foreground">Newest first</span>
      </div>

      <!--
        Newest first, with the value the asset actually carries called out and
        its move against the mark below it stated once. A reader gets the trend
        without reading down the column.
      -->
      <ul class="border border-border rounded-ds-lg overflow-hidden">
        <li
          v-for="(mark, index) in marks"
          :key="`${mark.date}-${index}`"
          class="flex flex-col sm:flex-row sm:items-center gap-4 px-4.5 py-3.5"
          :class="[
            mark === carrying ? 'bg-accent' : 'bg-card',
            index < marks.length - 1 ? 'border-b border-border' : '',
          ]"
        >
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span
                class="text-sm font-bold"
                :class="mark === carrying ? 'text-accent-foreground' : 'text-foreground'"
              >
                {{ dateLabel(mark.date) }}
              </span>
              <DSBadge
                v-if="mark === carrying"
                tone="brand"
                variant="soft"
                size="sm"
                class="uppercase tracking-ds-caps bg-card"
              >
                carrying value
              </DSBadge>
            </div>
            <p
              class="text-xs font-medium"
              :class="mark === carrying ? 'text-accent-foreground' : 'text-muted-foreground'"
            >
              {{ markCaption(mark) }}
            </p>
          </div>
          <div class="flex items-center gap-4">
            <div class="sm:text-right max-sm:flex-auto">
              <p
                class="text-lg font-bold tracking-ds-snug"
                :class="mark === carrying ? 'text-accent-foreground' : 'text-foreground'"
              >
                {{ money(mark.close) }}
              </p>
              <p
                v-if="change(index)"
                class="flex items-center sm:justify-end gap-1 text-xs font-bold"
                :class="change(index)!.isDown ? 'text-destructive' : 'text-success'"
              >
                <component
                  :is="change(index)!.isDown ? ArrowDown : ArrowUp"
                  class="size-3"
                />
                {{ change(index)!.label }} vs prior mark
              </p>
              <p
                v-else-if="index === marks.length - 1"
                class="text-xs font-medium text-muted-foreground"
              >
                First mark
              </p>
            </div>

            <DSButton
              type="button"
              variant="ghost"
              size="icon-sm"
              class="shrink-0 text-muted-foreground hover:bg-destructive-subtle hover:text-destructive-subtle-foreground"
              :aria-label="`Remove the value dated ${dateLabel(mark.date)}`"
              @click="handleRemove(mark)"
            >
              <Trash2 />
            </DSButton>
          </div>
        </li>
      </ul>
    </template>

    <!--
      Nothing recorded, and what that means: a step the user may walk past is
      one that has to say what happens if they do. Both offers are the two
      things they would otherwise type by hand.
    -->
    <div
      v-else
      class="flex flex-col items-center gap-1.5 px-6 py-8 text-center border border-dashed border-border rounded-ds-lg"
    >
      <ChartLine class="size-5 mb-1 text-muted-foreground" />
      <p class="text-sm font-bold text-foreground">
        No values recorded yet
      </p>
      <p class="max-w-md text-xs font-medium leading-normal text-muted-foreground">
        Skip this and the asset carries its purchase price until you record a mark. You can add
        appraisals any time from the asset page.
      </p>
      <div class="flex flex-wrap justify-center gap-2.5 mt-2.5">
        <DSButton
          v-if="purchase"
          type="button"
          variant="secondary"
          size="sm"
          @click="handleUsePurchase"
        >
          Use purchase price
        </DSButton>
        <DSButton
          type="button"
          variant="secondary"
          size="sm"
          @click="handleAddAppraisal"
        >
          Add an appraisal
        </DSButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, useId } from 'vue'
import { ArrowDown, ArrowUp, ChartLine, Info, Trash2 } from '@lucide/vue'

import {
  DSAmountField,
  DSBadge,
  DSButton,
  DSDateField,
  DSFieldLabel,
  DSSelect,
  DSSelectContent,
  DSSelectItem,
  DSSelectTrigger,
  DSSelectValue,
} from '@ds'

import { FRACTION_DIGITS } from '@/const'

import { AssetPriceSource } from '@/entities/assets/components/utils/enums'
import { ASSET_PRICE_SOURCE_EDITABLE } from '@/entities/assets/components/utils/const'

import { getCurrencySymbol } from '@/helpers/common'
import { getCurrentDate, stringToLocalDateString } from '@/helpers/dates'
import { numberFormat } from '@/helpers/numbers'

import { MARKS_FIELD } from '../utils/const'
import { carryingMark, markCaption, sortedMarks } from '../utils/helpers'
import { WizardMark } from '../utils/types'

/*
 * "What is it worth?" — the optional step, and the only one that collects a
 * list rather than a set of fields.
 *
 * A mark is a row of `asset_prices`: a date, a value and which of the two
 * editable sources it is. They are held in form state and written after the
 * holding exists — see `createMarks` — so leaving the step untouched costs
 * nothing and creates nothing.
 *
 * The kinds are the app's own, not a vocabulary invented here: Manual and
 * Override are what `asset_prices.source` accepts from a user, and what they do
 * to the timeline is the rule the prices tab already states.
 */
const { form } = defineProps<{
  form: any
}>()

/*
 * Wizard.vue hands every bespoke step the same bag — fields, origins, the
 * transaction type, the sentence. This step collects no fields (the rail counts
 * nothing here, which is what makes it optional) and reads what it needs off
 * the form, so the rest would otherwise land on the root div as attributes.
 */
defineOptions({ inheritAttrs: false })

/** The two a user may set. A connector's price is not a wizard's to write. */
const SOURCES = ASSET_PRICE_SOURCE_EDITABLE

const values = form.useSelector((state: any) => state.values)

const dateLabelId = useId()
const valueId = useId()
const sourceId = useId()

const valueCell = ref<HTMLElement>()

/*
 * The row being typed. Component state, not form state: it is not part of what
 * the wizard creates until "Add value" is pressed, and a half-typed value
 * sitting in the form would be a mark the summary counts.
 */
const draft = reactive<{ date: string; close?: number; source: AssetPriceSource }>({
  /*
   * The date the wizard already holds — the purchase — rather than today: the
   * first mark anyone records here is almost always the one they just entered a
   * price for, and a date they have to correct is worse than one they confirm.
   */
  date: form.state.values.date || getCurrentDate({ iso: true }),
  source: AssetPriceSource.MANUAL,
})

const currency = computed<string>(() => values.value.currency ?? '')

/** The wizard's own purchase price, when it has one to offer. */
const purchase = computed(() => {
  const close = values.value.amount
  const date = values.value.date
  if (typeof close !== 'number' || !date) return undefined
  return { close, date }
})

/* Newest first, and which of them the asset carries — both shared with the
 * summary, which reads the same list back. */
const marks = computed<WizardMark[]>(() => sortedMarks(values.value[MARKS_FIELD]))

const carrying = computed<WizardMark | undefined>(() => carryingMark(marks.value))

const canAdd = computed(() => Boolean(draft.date) && typeof draft.close === 'number')

const setMarks = (next: WizardMark[]) => {
  form.setFieldValue(MARKS_FIELD, next)
}

const dateLabel = (date: string) => stringToLocalDateString(date) ?? date

const money = (close: number) =>
  numberFormat(close, {
    currency: getCurrencySymbol(currency.value),
    fractionDigits: FRACTION_DIGITS,
  })

/*
 * The move against the mark below, stated on the row above it. Nothing when
 * there is nothing below, and nothing when the prior mark is zero — a move
 * against nothing is not a percentage.
 */
const change = (index: number) => {
  const mark = marks.value[index]
  const prior = marks.value[index + 1]
  if (!prior || !prior.close) return undefined

  const ratio = ((mark.close - prior.close) / Math.abs(prior.close)) * 100
  if (!ratio) return undefined

  return {
    isDown: ratio < 0,
    label: `${numberFormat(Math.abs(ratio), { fractionDigits: 1 })}%`,
  }
}

/*
 * One mark per date: a second value for a day the list already has is an edit
 * of that day, not a second history entry — and `asset_prices` would keep both.
 */
const handleAdd = () => {
  if (!canAdd.value) return

  const mark: WizardMark = {
    date: draft.date,
    close: draft.close!,
    source: draft.source,
  }

  setMarks([...marks.value.filter(item => item.date !== mark.date), mark])
  draft.close = undefined
}

const handleRemove = (mark: WizardMark) => {
  setMarks(marks.value.filter(item => item !== mark))
}

/*
 * The purchase price as the first mark, dated the day it was paid. Marked as
 * such on the row: it is a value the user gave the wizard rather than one they
 * entered here, and the difference is the whole point of the caption.
 */
const handleUsePurchase = () => {
  if (!purchase.value) return

  setMarks([
    ...marks.value.filter(item => item.date !== purchase.value!.date),
    {
      date: purchase.value.date,
      close: purchase.value.close,
      source: AssetPriceSource.MANUAL,
      fromPurchase: true,
    },
  ])
}

/*
 * The other half of the empty state: an appraisal is entered in the card above,
 * so the offer puts the cursor in it rather than opening anything new.
 */
const handleAddAppraisal = () => {
  valueCell.value?.querySelector('input')?.focus()
}
</script>
